import React from "react";

export function PensionReformDetails() {
  return (
    <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4 mt-4">
      <h4 className="font-medium text-gray-900 mb-3">Pension Age Reform — Prediction Task</h4>

      <div className="space-y-4 text-sm text-gray-800">
        <section>
          <h5 className="font-semibold text-gray-900">The Setting</h5>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong>A public pension program.</strong> Workers who reach age 63 can stop working and begin receiving monthly pension payments funded by payroll taxes collected during their working years. The pension provides enough income for a modest but adequate standard of living in retirement.
            </li>
            <li>
              <strong>A disability benefit program.</strong> Workers of <em>any</em> age who develop a medical condition that prevents them from performing their job can apply for disability benefits. To qualify, an applicant must be evaluated by a government-appointed physician who certifies that the worker is medically unable to continue in their current type of work. Disability benefits provide roughly the same monthly income as the pension. This program exists separately from the pension and is not affected by the reform described below.
            </li>
          </ol>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">The Reform</h5>
          <p>
            Facing rising costs from an aging population, the government passes a law gradually raising the pension eligibility age from <strong>63 to 65</strong>. Under the new rules, workers must wait two additional years before they can claim their pension.
          </p>
          <p>
            The reform is phased in over several years. No other programs or policies are changed.
          </p>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">The Workforce</h5>
          <p>
            The country's workforce includes people in a wide range of occupations. For this question, consider two broad groups of workers affected by the reform — those who, under the old rules, would have retired at 63 but must now wait until 65:
          </p>
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-gray-900">Group P — Physically demanding occupations</p>
              <p>
                These workers hold jobs that place significant strain on the body. Examples include construction laborers, factory assembly workers, warehouse workers, long-haul truck drivers, and nursing home aides. Many of these workers develop chronic pain, joint problems, or fatigue-related conditions by their early 60s. At the same time, these jobs are often lower-paid, meaning these workers have less personal savings and depend heavily on the pension to make ends meet. They face strong financial pressure to keep earning income until they can claim benefits.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">Group D — Desk-based occupations</p>
              <p>
                These workers hold jobs that are not physically taxing. Examples include accountants, administrative assistants, insurance adjusters, IT support staff, and government clerks. These workers are less likely to develop physical health problems from their work. They also tend to earn somewhat higher salaries and are more likely to have some personal savings, though most still rely on the pension as a major source of retirement income.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">Your Task</h5>
          <p>
            After the reform took effect, researchers tracked what happened to workers in both groups during the ages of <strong>63 to 65</strong> — the new "gap years" when they would previously have been retired but now must wait for their pension.
          </p>
          <p>
            Specifically, researchers measured the <strong>increase in employment rates</strong> — that is, how many more workers in each group were still working at ages 63–65 compared to before the reform, when most would have already retired.
          </p>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">Prediction Question</h5>
          <p className="mb-2">
            <strong>Which group showed a larger increase in employment rates at ages 63–65 after the reform?</strong>
          </p>
          <div className="space-y-2">
            <p>
              <strong>Group P</strong> (physically demanding jobs) showed the larger increase in employment.
            </p>
            <p>
              <strong>Group D</strong> (desk-based jobs) showed the larger increase in employment.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
